import type { GuiElement } from "../types";
import type { GuiSlice, GuiState, ViewerSliceCreator } from "./types";

export const guiInitialState: GuiState = {
  guiElements: {},
  guiContainerChildren: { root: new Set() },
};

export const createGuiSlice: ViewerSliceCreator<GuiSlice> = (set) => ({
  ...guiInitialState,

  addGuiFolder: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "folder",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        label: msg.label,
        expand_by_default: msg.expand_by_default,
      };
      state.guiElements[msg.uuid] = element;

      // Add to container children
      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);

      // Initialize children set for this folder
      if (!state.guiContainerChildren[msg.uuid]) {
        state.guiContainerChildren[msg.uuid] = new Set();
      }
    }),

  addGuiButton: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "button",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        label: msg.label,
        disabled: msg.disabled,
        color: msg.color,
      };
      state.guiElements[msg.uuid] = element;

      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);
    }),

  addGuiSlider: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "slider",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        label: msg.label,
        min: msg.min,
        max: msg.max,
        step: msg.step,
        value: msg.initial_value,
        disabled: msg.disabled,
      };
      state.guiElements[msg.uuid] = element;

      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);
    }),

  addGuiDropdown: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "dropdown",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        label: msg.label,
        options: msg.options,
        value: msg.initial_value,
        disabled: msg.disabled,
      };
      state.guiElements[msg.uuid] = element;

      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);
    }),

  addGuiCheckbox: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "checkbox",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        label: msg.label,
        value: msg.initial_value,
        disabled: msg.disabled,
      };
      state.guiElements[msg.uuid] = element;

      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);
    }),

  addGuiText: (msg) =>
    set((state) => {
      const element: GuiElement = {
        uuid: msg.uuid,
        type: "text",
        container_uuid: msg.container_uuid,
        panel: msg.panel,
        order: msg.order,
        content: msg.content,
      };
      state.guiElements[msg.uuid] = element;

      if (!state.guiContainerChildren[msg.container_uuid]) {
        state.guiContainerChildren[msg.container_uuid] = new Set();
      }
      state.guiContainerChildren[msg.container_uuid].add(msg.uuid);
    }),

  removeGui: (uuid) =>
    set((state) => {
      const element = state.guiElements[uuid];
      if (!element) return;

      // Remove from parent container
      const container = state.guiContainerChildren[element.container_uuid];
      if (container) {
        container.delete(uuid);
      }

      // Remove children if folder
      const children = state.guiContainerChildren[uuid];
      if (children) {
        for (const childUuid of children) {
          delete state.guiElements[childUuid];
        }
        delete state.guiContainerChildren[uuid];
      }

      delete state.guiElements[uuid];
    }),

  updateGui: (uuid, updates) =>
    set((state) => {
      const element = state.guiElements[uuid];
      if (!element) return;

      for (const [key, value] of Object.entries(updates)) {
        if (key === "value") {
          if ("value" in element) {
            (element as { value: unknown }).value = value;
          }
        } else if (key in element) {
          (element as Record<string, unknown>)[key] = value;
        }
      }
    }),

  updateGuiValue: (uuid, value) =>
    set((state) => {
      const element = state.guiElements[uuid];
      if (!element || !("value" in element)) return;
      (element as { value: unknown }).value = value;
    }),
});
